import React, { useState } from 'react';
import { useInventory } from '../context/InventoryContext';
import { useAuth } from '../context/AuthContext';
import BulkUpload from '../components/BulkUpload';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import { toast } from 'react-toastify';

const SubCompany = () => {
  const { subCompanies, addSubCompany, updateSubCompany, deleteSubCompany, companies } = useInventory();
  const { currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [form, setForm] = useState({
    name: '', address: '', contactPerson: '', phone: '', parentCompanyId: isSuperAdmin ? '' : currentUser.companyId
  });
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});

  // Store original values for comparison when editing
  const [originalFormValues, setOriginalFormValues] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [searchQuery, setSearchQuery] = useState('');

  // Helper function to check if any field was actually changed
  const hasChanges = (original, current) => {
    if (!original) return false;
    // Compare all fields
    const fieldsToCompare = ['name', 'address', 'contactPerson', 'phone', 'parentCompanyId'];
    
    for (let field of fieldsToCompare) {
      const originalValue = (original[field] || '').trim();
      const currentValue = (current[field] || '').trim();
      if (originalValue !== currentValue) {
        return true;
      }
    }
    
    return false;
  };

  const visibleSubCompanies = isSuperAdmin
    ? subCompanies
    : subCompanies.filter(s => s.parentCompanyId === currentUser.companyId);

  const filteredSubCompanies = visibleSubCompanies.filter(sub => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const parentName = companies.find(c => c.id === sub.parentCompanyId)?.name || '';
    return (
      sub.name.toLowerCase().includes(q) ||
      parentName.toLowerCase().includes(q) ||
      (sub.contactPerson || '').toLowerCase().includes(q) ||
      (sub.phone || '').includes(q)
    );
  });

  const totalItems = filteredSubCompanies.length;
  const paginatedSubs = filteredSubCompanies.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  React.useEffect(() => {
    setCurrentPage(1);
  }, [isSuperAdmin, currentUser?.companyId]);

  const resetForm = () => {
    setForm({
      name: '', address: '', contactPerson: '', phone: '',
      parentCompanyId: isSuperAdmin ? '' : currentUser.companyId
    });
    setEditingId(null);
    setShowForm(false);
    setErrors({});
    setOriginalFormValues(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setErrors({ name: 'Name required' });
      return;
    }
    
    const data = {
      ...form,
      parentCompanyId: isSuperAdmin ? form.parentCompanyId : currentUser.companyId
    };
    
    if (editingId) {
      // Check if anything was actually changed
      if (!hasChanges(originalFormValues, form)) {
        setErrors({});
        resetForm();
        toast.info('No changes were made to the sub-company.');
        return;
      }
      
      updateSubCompany(editingId, data);
      toast.success(`✅ Sub-company "${form.name.trim()}" has been updated successfully!`);
    } else {
      addSubCompany(data);
      toast.success(`✅ Sub-company "${form.name.trim()}" has been added successfully!`);
    }
    resetForm();
  };

  const handleEdit = (sub) => {
    const formValues = {
      name: sub.name || "",
      address: sub.address || "",
      contactPerson: sub.contactPerson || "",
      phone: sub.phone || "",
      parentCompanyId: sub.parentCompanyId || ""
    };
    
    // Store original values for comparison
    setOriginalFormValues({
      name: sub.name || "",
      address: sub.address || "",
      contactPerson: sub.contactPerson || "",
      phone: sub.phone || "",
      parentCompanyId: sub.parentCompanyId || ""
    });
    
    setForm(formValues);
    setEditingId(sub.id);
    setShowForm(true);
  };

  const handleDelete = (subId) => {
    const subToDelete = subCompanies.find(s => s.id === subId);
    const subName = subToDelete?.name || 'Unknown';
    
    if (window.confirm(`Delete sub-company "${subName}"?`)) {
      deleteSubCompany(subId);
      toast.success(`Sub-company "${subName}" has been deleted successfully!`);
    }
  };

  // Validation function for bulk upload
  const validateSubCompanyRow = (row, index, allRows) => {
    const errs = [];
    const name = (row.name || '').trim();
    
    if (!name) {
      errs.push({ column: 'Sub-Company Name', message: 'Required' });
    } else {
      // Check for duplicates in database (only for visible sub-companies)
      const existingSub = visibleSubCompanies.some(s => s.name.toLowerCase() === name.toLowerCase());
      if (existingSub) {
        errs.push({ column: 'Sub-Company Name', message: 'Sub-company name already exists' });
      }
      
      // Check for duplicates within the file
      const earlierDuplicate = allRows.slice(0, index).some(
        r => (r.name || '').trim().toLowerCase() === name.toLowerCase()
      );
      if (earlierDuplicate) {
        errs.push({ column: 'Sub-Company Name', message: 'Duplicate name within this file' });
      }
    }
    
    if (row.phone && !/^\d{10}$/.test(row.phone)) {
      errs.push({ column: 'Phone', message: 'Must be exactly 10 digits' });
    }
    
    // Validate parent company if super admin
    if (isSuperAdmin) {
      const parentCompanyName = (row.parentCompanyName || '').trim();
      if (parentCompanyName) {
        const parentCompany = companies.find(c => c.name.toLowerCase() === parentCompanyName.toLowerCase());
        if (!parentCompany) {
          errs.push({ column: 'Parent Company Name', message: 'Parent company not found in database' });
        }
      } else {
        errs.push({ column: 'Parent Company Name', message: 'Parent company is required' });
      }
    }
    
    return errs;
  };

  // Handle bulk upload with proper mapping
  const handleBulkUpload = (rows) => {
    rows.forEach(row => {
      const subCompanyData = {
        name: row.name || '',
        address: row.address || '',
        contactPerson: row.contactPerson || '',
        phone: row.phone || '',
        parentCompanyId: isSuperAdmin 
          ? companies.find(c => c.name.toLowerCase() === (row.parentCompanyName || '').trim().toLowerCase())?.id 
          : currentUser.companyId
      };
      
      // Only add if parent company is found (for super admin) or for non-super admin
      if (isSuperAdmin && !subCompanyData.parentCompanyId) {
        console.warn(`Skipping sub-company "${row.name}" - Parent company not found`);
        return;
      }
      
      addSubCompany(subCompanyData);
    });
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Sub‑Companies</h1>
        <div className="flex items-center space-x-3">
          <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary flex items-center space-x-2">
            <FaPlus /> <span>Add Sub‑Company</span>
          </button>
          
          <BulkUpload
            title="SubCompany"
            downloadLabel="Download Add Template"
            uploadLabel="Bulk Add"
            downloadData={visibleSubCompanies.map(s => {
              const data = {
                name: s.name,
                address: s.address || '',
                contactPerson: s.contactPerson || '',
                phone: s.phone || ''
              };
              // Add parent company name for super admin
              if (isSuperAdmin) {
                const parentCompany = companies.find(c => c.id === s.parentCompanyId);
                data.parentCompanyName = parentCompany?.name || '';
              }
              return data;
            })}
            columns={isSuperAdmin ? [
              { key: 'name', label: 'Sub-Company Name' },
              { key: 'address', label: 'Address' },
              { key: 'contactPerson', label: 'Contact Person' },
              { key: 'phone', label: 'Phone' },
              { key: 'parentCompanyName', label: 'Parent Company Name' }
            ] : [
              { key: 'name', label: 'Sub-Company Name' },
              { key: 'address', label: 'Address' },
              { key: 'contactPerson', label: 'Contact Person' },
              { key: 'phone', label: 'Phone' }
            ]}
            sampleRow={isSuperAdmin ? 
              { name: 'Example SubCo', address: '123 St', contactPerson: 'John', phone: '9999999999', parentCompanyName: 'Example Co' } :
              { name: 'Example SubCo', address: '123 St', contactPerson: 'John', phone: '9999999999' }
            }
            onUpload={handleBulkUpload}
            validateRow={validateSubCompanyRow}
          />
        </div>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name, parent company, contact, phone…"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full max-w-md rounded-xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Address</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contact Person</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Phone</th>
                {isSuperAdmin && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Parent Company</th>}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {paginatedSubs.map(s => (
                <tr key={s.id} className="hover:bg-indigo-50/50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{s.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{s.address || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{s.contactPerson || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{s.phone || '-'}</td>
                  {isSuperAdmin && (
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {s.parentCompanyName || "-"}
                    </td>
                  )}
                  <td className="px-4 py-3 text-sm">
                    <div className="flex space-x-2">
                      <button onClick={() => handleEdit(s)} className="text-indigo-600 hover:text-indigo-900 transition">
                        <FaEdit />
                      </button>
                      <button onClick={() => handleDelete(s.id)} className="text-red-500 hover:text-red-700 transition">
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {paginatedSubs.length === 0 && (
                <tr>
                  <td colSpan={isSuperAdmin ? "6" : "5"} className="px-4 py-8 text-center text-sm text-gray-500">
                    No sub‑companies added.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Pagination
        totalItems={totalItems}
        pageSize={pageSize}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1); 
        }}
      />

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white p-6 rounded-2xl shadow-2xl w-full max-w-lg mx-auto my-8 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{editingId ? 'Edit Sub‑Company' : 'Add Sub‑Company'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})}
                />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.address}
                  onChange={e => setForm({...form, address: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.contactPerson}
                  onChange={e => setForm({...form, contactPerson: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone (10 digits)</label>
                <input
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  value={form.phone}
                  onChange={e => setForm({...form, phone: e.target.value.replace(/\D/g, '')})}
                  maxLength={10}
                />
              </div>
              {isSuperAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Company</label>
                  <select
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
                    value={form.parentCompanyId}
                    onChange={e => setForm({...form, parentCompanyId: e.target.value})}
                    required
                  >
                    <option value="">Select Company</option>
                    {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
              {!isSuperAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parent Company</label>
                  <input
                    className="w-full rounded-xl border border-gray-300 px-4 py-2.5 bg-gray-100 text-gray-600"
                    value={companies.find(c => c.id === currentUser.companyId)?.name || ''}
                    disabled
                  />
                </div>
              )}
              <div className="flex justify-end space-x-3 pt-4">
                <button type="button" onClick={resetForm} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary">{editingId ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubCompany;